import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Clock, ArrowRight, Calendar, User, Tag } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import WaveOverlay from '../components/WaveOverlay';
import { sendNewsletterSubscription } from '../services/emailService';

const Blogs = ({ onBookMeeting }) => {
  const blogPosts = [
    {
      title: "10 Essential SEO Tips for Real Estate Agents",
      excerpt: "Discover the most effective SEO strategies to help your real estate business rank higher in local search results and attract more qualified leads.",
      category: "SEO",
      readTime: "5 min read",
      date: "March 15, 2025",
      image: "/api/placeholder/400/250",
      featured: true
    },
    {
      title: "Social Media Marketing for Mortgage Brokers",
      excerpt: "Learn how to leverage social media platforms to build trust, showcase your expertise, and generate more mortgage leads.",
      category: "Social Media",
      readTime: "7 min read",
      date: "March 12, 2025",
      image: "/api/placeholder/400/250"
    },
    {
      title: "The Complete Guide to Google Business Profile Optimization",
      excerpt: "Master the art of Google Business Profile optimization to improve your local visibility and attract more customers.",
      category: "Local SEO",
      readTime: "8 min read",
      date: "March 10, 2025",
      image: "/api/placeholder/400/250"
    },
    {
      title: "Building Trust Through Online Reviews",
      excerpt: "Understand the importance of online reviews and learn strategies to encourage positive feedback from your clients.",
      category: "Reputation Management",
      readTime: "6 min read",
      date: "March 8, 2025",
      image: "/api/placeholder/400/250"
    },
    {
      title: "Website Design Trends for Real Estate in 2025",
      excerpt: "Stay ahead of the curve with the latest website design trends that convert visitors into clients.",
      category: "Web Design",
      readTime: "9 min read",
      date: "March 5, 2025",
      image: "/api/placeholder/400/250"
    },
    {
      title: "Email Marketing Strategies That Actually Work",
      excerpt: "Discover proven email marketing tactics to nurture leads and maintain relationships with past clients.",
      category: "Email Marketing",
      readTime: "6 min read",
      date: "March 3, 2025",
      image: "/api/placeholder/400/250"
    }
  ];

  const categories = ["All", "SEO", "Social Media", "Local SEO", "Reputation Management", "Web Design", "Email Marketing"];

  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setSubscribeStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setIsSubscribing(true);
    setSubscribeStatus(null);

    try {
      const result = await sendNewsletterSubscription(email);
      
      if (result.success) {
        setSubscribeStatus({ type: 'success', message: result.message });
        setIsSubscribed(true); // Hide the form
        setEmail('');
      } else {
        setSubscribeStatus({ type: 'error', message: result.message });
      }
    } catch (error) {
      setSubscribeStatus({ 
        type: 'error', 
        message: 'An unexpected error occurred. Please try again.' 
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleSubscribeAgain = () => {
    setIsSubscribed(false);
    setSubscribeStatus(null);
  };

  return (
    <>
      <Helmet>
        <title>Digital Marketing Blog | Tips and Insights | Insight Digital Agency</title>
        <meta name="description" content="Expert insights on digital marketing, local SEO, AI automation, and small business growth. Actionable tips from Insight Digital Agency." />
        <link rel="canonical" href="https://insightdigitalagency.com/blogs" />
        <meta property="og:title" content="Digital Marketing Blog | Insight Digital Agency" />
        <meta property="og:description" content="Expert insights on digital marketing, local SEO, AI automation, and small business growth." />
        <meta property="og:url" content="https://insightdigitalagency.com/blogs" />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection textOnly={true}>
        <div className="max-w-4xl mx-auto text-center space-y-6 relative z-40">
          <div className="flex items-center justify-center space-x-2 text-orange-300">
            <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Insight Digital Agency
            </span>
            <span>» Blogs</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold">Our Blogs</h1>
          <p className="text-xl text-blue-100 leading-relaxed">
            Stay informed, inspired, and ahead of the curve! Dive into our blog section to discover actionable tips, 
            in-depth analyses, and compelling stories that empower you to thrive in the ever-evolving world of digital marketing.
          </p>
        </div>
      </HeroSection>

      {/* Coming Soon Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            <div className="w-24 h-24 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <h2 className="text-3xl font-bold text-gray-900">Coming Soon</h2>
            <p className="text-xl text-gray-600">
              We're working hard to bring you valuable insights and actionable tips. Our blog will be launching soon 
              with expert content on digital marketing, SEO, social media, and more.
            </p>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg">
              Get Notified When We Launch
            </Button>
          </div>
        </div>
      </section>

      {/* Preview Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">What to Expect</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Here's a preview of the type of content we'll be sharing to help your business succeed online
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category, index) => (
              <Button
                key={index}
                variant={index === 0 ? "default" : "outline"}
                className={index === 0 ? "bg-blue-600 hover:bg-blue-700" : ""}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Featured Post */}
          {blogPosts.filter(post => post.featured).map((post, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden mb-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="h-64 lg:h-auto bg-gradient-to-br from-[#1074ad] to-[#163760]"></div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold">
                      {post.category}
                    </span>
                    <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-sm font-semibold">
                      Featured
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{post.title}</h3>
                  <p className="text-gray-600 mb-6">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>{post.date}</span>
                      <div className="flex items-center space-x-1">
                        <Clock size={16} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <Button variant="outline" className="flex items-center space-x-2">
                      <span>Read More</span>
                      <ArrowRight size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.filter(post => !post.featured).map((post, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 bg-gradient-to-br from-[#1074ad] to-[#163760]"></div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold">
                      {post.category}
                    </span>
                    <div className="flex items-center space-x-1 text-sm text-gray-500">
                      <Clock size={14} />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{post.title}</h3>
                  <p className="text-gray-600 mb-4 text-sm">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{post.date}</span>
                    <Button variant="outline" size="sm" className="flex items-center space-x-1">
                      <span>Read</span>
                      <ArrowRight size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold text-gray-900">Stay Updated</h2>
            <p className="text-xl text-gray-600">
              Subscribe to our newsletter to get the latest insights and tips delivered directly to your inbox.
            </p>
            
            {isSubscribed ? (
              /* Thank You Message */
              <div className="text-center">
                <div className="p-8 rounded-xl border-l-4 max-w-md mx-auto bg-green-50 border-l-green-500 border border-green-200">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 bg-green-500">
                      <span className="text-white text-lg">✓</span>
                    </div>
                    <div className="text-green-800 text-center">
                      <h3 className="text-xl font-bold mb-2">Thank You for Subscribing!</h3>
                      <p className="font-medium mb-3">{subscribeStatus?.message}</p>
                      <p className="text-sm text-green-600 mb-4">
                        You'll receive valuable insights and digital marketing tips.
                      </p>
                      <Button 
                        onClick={handleSubscribeAgain} 
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
                      >
                        Subscribe Another Email
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Error Messages Only */}
                {subscribeStatus && subscribeStatus.type === 'error' && (
                  <div className="p-6 rounded-xl border-l-4 max-w-md mx-auto bg-red-50 border-l-red-500 border border-red-200">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mr-3 bg-red-500">
                        <span className="text-white text-sm">✕</span>
                      </div>
                      <div className="text-red-800">
                        <p className="font-medium">{subscribeStatus.message}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Button 
                    type="submit"
                    disabled={isSubscribing}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg"
                  >
                    {isSubscribing ? 'Subscribing...' : 'Subscribe'}
                  </Button>
                </form>
              </>
            )}
            <p className="text-sm text-gray-500">
              No spam, unsubscribe at any time. We respect your privacy.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 hero-gradient text-white relative">
        <WaveOverlay inverted={true} color="fill-white" />
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            While you wait for our blog to launch, let's discuss how we can help transform your digital presence today.
          </p>
          <Button 
            onClick={onBookMeeting}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg"
          >
            Book a Meeting
          </Button>
        </div>
        <WaveOverlay />
      </section>
    </div>
    </>
  );
};

export default Blogs;

